import { useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import type {
  AgentTemplate,
  AgentTemplateListItem,
  TemplateCategory,
} from '@/schemas/agent-template'
import { TEMPLATE_CATEGORIES } from '@/schemas/agent-template'
import { agentTemplateService } from '@/services/agent-template'
import { useAgentWizardStore } from '@/stores/agent-wizard-store'
import { TemplateCard } from '../template-card'
import { TemplatePreview } from '../template-preview'
import { Button } from '@/components/ui/button'

export function StepTemplateSelect() {
  const { t } = useTranslation('agents')
  const { setValue } = useFormContext<AgentWizardData>()
  const { selectedTemplate, selectTemplate, applyTemplate, skipTemplateStep } =
    useAgentWizardStore()

  const [templates, setTemplates] = useState<AgentTemplateListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<TemplateCategory>('all')
  const [previewTemplate, setPreviewTemplate] = useState<AgentTemplate | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  // Load templates function
  const loadTemplates = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await agentTemplateService.listTemplates()
      setTemplates(response.templates)
    } catch (err) {
      console.error('Error loading templates:', err)
      setError(t('wizard.template.error'))
    } finally {
      setIsLoading(false)
    }
  }

  // Load templates on mount
  useEffect(() => {
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filter templates by category
  const filteredTemplates =
    activeCategory === 'all'
      ? templates
      : templates.filter((t) => t.category === activeCategory)

  // Apply template values to react-hook-form
  const applyTemplateToForm = (template: AgentTemplate) => {
    // Update store
    applyTemplate(template)

    // Sync with react-hook-form
    setValue('selected_template_id', template.id)
    setValue('description', template.description)
    setValue('icon', template.icon)
    setValue('provider', template.llm_config?.provider ?? null)
    setValue('model', template.llm_config?.model ?? null)
    setValue('temperature', template.llm_config?.temperature ?? 0.7)
    setValue('max_tokens', template.llm_config?.max_tokens ?? 2048)
    setValue('system_prompt', template.system_prompt)
    setValue('memory_type', template.memory_config?.type ?? null)
    setValue('memory_max_tokens', template.memory_config?.max_tokens ?? null)
    setValue('memory_window_size', template.memory_config?.window_size ?? null)
    setValue('timeout', template.settings?.timeout ?? 60)
    setValue('max_retries', template.settings?.max_retries ?? 3)
    setValue('max_iterations', template.settings?.max_iterations ?? 10)
  }

  // Handle template selection - fetch full template and apply values
  const handleSelectTemplate = async (template: AgentTemplateListItem) => {
    if (selectedTemplate?.id === template.id) {
      // Deselect if already selected
      selectTemplate(null)
      setValue('selected_template_id', null)
      return
    }

    // Fetch full template data and apply it
    setIsLoadingPreview(true)
    try {
      const fullTemplate = await agentTemplateService.getTemplate(template.id)
      applyTemplateToForm(fullTemplate)
    } catch (err) {
      console.error('Error loading template:', err)
      setError(t('wizard.template.error'))
    } finally {
      setIsLoadingPreview(false)
    }
  }

  // Handle preview click
  const handlePreview = async (template: AgentTemplateListItem) => {
    setIsLoadingPreview(true)
    try {
      const fullTemplate = await agentTemplateService.getTemplate(template.id)
      setPreviewTemplate(fullTemplate)
    } catch (err) {
      console.error('Error loading template:', err)
      setError(t('wizard.template.error'))
    } finally {
      setIsLoadingPreview(false)
    }
  }

  // Handle use template from preview
  const handleUseTemplate = () => {
    if (previewTemplate) {
      applyTemplateToForm(previewTemplate)
      setPreviewTemplate(null)
    }
  }

  // Handle create from scratch
  const handleCreateFromScratch = () => {
    skipTemplateStep()
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
          <p className="text-gray-400">{t('wizard.template.loading')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-8 w-8 text-red-400" />
          <p className="text-red-400">{error}</p>
          <Button
            type="button"
            variant="outline"
            onClick={loadTemplates}
            className="border-gray-700 text-gray-300 hover:bg-white/10"
          >
            {t('wizard.template.tryAgain')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(TEMPLATE_CATEGORIES).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveCategory(key as TemplateCategory)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
              activeCategory === key
                ? 'bg-indigo-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Create from scratch card */}
        <div
          onClick={handleCreateFromScratch}
          className={cn(
            'relative cursor-pointer rounded-xl p-4 transition-all duration-200',
            'border-2 border-dashed border-gray-700/50 bg-transparent',
            'hover:border-indigo-500/50 hover:bg-indigo-500/5',
            'flex flex-col items-center justify-center min-h-[160px]'
          )}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-800 text-gray-400 mb-3">
            <Plus className="h-6 w-6" />
          </div>
          <h3 className="font-medium text-white mb-1">{t('wizard.template.createFromScratch')}</h3>
          <p className="text-xs text-gray-400 text-center">
            {t('wizard.template.createFromScratchDesc')}
          </p>
        </div>

        {/* Template cards */}
        {filteredTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate?.id === template.id}
            onSelect={() => handleSelectTemplate(template)}
            onPreview={() => handlePreview(template)}
          />
        ))}
      </div>

      {/* No templates message */}
      {filteredTemplates.length === 0 && activeCategory !== 'all' && (
        <div className="text-center py-8">
          <p className="text-gray-400">
            {t('wizard.template.noTemplatesInCategory')}
          </p>
        </div>
      )}

      {/* Help text */}
      <div className="pt-4 border-t border-gray-700/50">
        <p className="text-xs text-gray-500">
          {t('wizard.template.helpText')}
        </p>
      </div>

      {/* Loading preview overlay */}
      {isLoadingPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
        </div>
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onUseTemplate={handleUseTemplate}
        />
      )}
    </div>
  )
}
